import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@discovr/common';
import { safeRedirectPath } from './redirect';
import { hardNavigate } from './hardNavigate';
import { usePendingClaimReturnPath } from './pendingClaimReturn';

/**
 * Once authenticated, forward to the attempted deep link (`redirect` param), else
 * to a Shopify claim still pending in this tab, else to `/start`, which picks the
 * Brand Brain setup or the campaigns. Uses a full navigation
 * for a real destination so both pathname and query are restored — navigate({to})
 * would drop the ?tab=… view state.
 */
export function usePostLoginRedirect(redirect: string | undefined): void {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const pendingClaimPath = usePendingClaimReturnPath();
  const hasNavigated = useRef(false);

  useEffect(() => {
    if (loading || !user || hasNavigated.current) return;
    hasNavigated.current = true;
    const explicit = safeRedirectPath(redirect ?? null);
    const dest = explicit && explicit !== '/' ? explicit : pendingClaimPath;
    if (dest) hardNavigate(dest);
    else navigate({ to: '/start', replace: true });
  }, [loading, user, redirect, navigate, pendingClaimPath]);
}
